import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from './Modal';
import ClosingX from './ClosingX';
import BButton from './BButton.mjs';
import InlineBanner from './common/InlineBanner';
import { useConfirm } from '../context/ConfirmContext';
import * as FileService from '../services/FileService.mjs';
import { notify } from '../notifications/errorBus';
import "../css/UploadFileModal.css"; // We borrow the modal layout structure
import "../css/DuplicateManagerModal.css";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faTrash, faCheckCircle, faRotate, faMagnifyingGlass,
    faImage, faFingerprint, faClone, faDatabase, faBoxArchive, faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";

// ── Pure helpers (module scope so they aren't re-created per render) ──────────

/** Human-readable byte size. */
function formatBytes(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
    const val = n / Math.pow(1024, i);
    return `${val >= 100 || i === 0 ? Math.round(val) : val.toFixed(1)} ${units[i]}`;
}

/** Tolerant timestamp → millis (ISO string / Date / Firestore Timestamp / epoch). */
function toMillis(value) {
    if (value == null) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
        const ms = Date.parse(value);
        return Number.isNaN(ms) ? 0 : ms;
    }
    if (value instanceof Date) return value.getTime();
    if (typeof value.toMillis === "function") return value.toMillis();
    if (typeof value._seconds === "number") return value._seconds * 1000;
    return 0;
}

function formatDate(value) {
    const ms = toMillis(value);
    return ms ? new Date(ms).toLocaleDateString() : "—";
}

/** Coarse file category from a mimetype, for the storage breakdown. */
function categoryOf(mimetype) {
    const m = (mimetype || "").toLowerCase();
    if (m.startsWith("image/")) return "Images";
    if (m.startsWith("audio/")) return "Audio";
    if (m.startsWith("video/")) return "Video";
    if (m.startsWith("text/") || m.includes("pdf") || m.includes("document")) return "Documents";
    if (m.includes("zip") || m.includes("tar") || m.includes("compressed") || m.includes("rar")) return "Archives";
    return "Other";
}

// A categorical (data-viz) palette for the storage-by-type bar. Images/Other/
// Archives map to semantic tokens; the remaining hues are a distinct chart
// scale with no semantic equivalent and read on both light and dark themes.
const CATEGORY_COLORS = {
    Images: "var(--qd-accent)",
    Audio: "#9b6dff",
    Video: "#ff6d9b",
    Documents: "#37b6ff",
    Archives: "var(--qd-warning)",
    Other: "var(--qd-text-subtle)",
};

// Cap how many rows we render at once so a workspace with thousands of broken
// files can't build a 10k-node DOM. Bulk actions still operate on every file.
const ROW_CAP = 250;

/** A single overview metric tile. Hoisted to module scope so it isn't a fresh
 *  component type on every parent render (which would remount the subtree on
 *  every search keystroke / progress tick). */
function StatCard({ icon, value, label, tone, onClick }) {
    return (
        <button className={`dm-stat ${tone || ''} ${onClick ? 'clickable' : ''}`} onClick={onClick} disabled={!onClick}>
            <FontAwesomeIcon icon={icon} className="dm-stat-icon" />
            <span className="dm-stat-value">{value}</span>
            <span className="dm-stat-label">{label}</span>
        </button>
    );
}

const DuplicateManagerModal = ({ onClose }) => {
    const confirm = useConfirm();
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [processingIds, setProcessingIds] = useState(() => new Set());
    const [activeTab, setActiveTab] = useState('overview');
    const [search, setSearch] = useState('');
    // Per-content-hash override of which copy to keep (defaults to the oldest).
    const [keeperByHash, setKeeperByHash] = useState({});
    // Progress for long-running bulk operations: { label, done, total }.
    const [bulk, setBulk] = useState(null);

    // ── Loading ──────────────────────────────────────────────────────────────
    const loadFiles = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await FileService.getFiles();
            setFiles(Array.isArray(data) ? data : []);
        } catch (err) {
            // The axios interceptor already surfaced the full error globally;
            // here we just flip the panel into a retryable error state.
            setLoadError(err?.response?.data?.message || err?.message || "Could not load files.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadFiles(); }, [loadFiles]);

    // ── Derived state (memoized on `files`) ──────────────────────────────────
    const stats = useMemo(() => {
        const nameOf = (f) => f?.metadata?.name || "Untitled";
        const sizeOf = (f) => Number(f?.metadata?.size) || 0;

        const missingHash = files.filter(f => !f.hash);
        const missingThumb = files.filter(f => !f.thumbnail);
        const incompleteMeta = files.filter(f =>
            !f?.metadata?.name || !f?.metadata?.mimetype || !(Number(f?.metadata?.size) > 0)
        );

        // Group by content hash → duplicate groups (>1 copy).
        const byHash = files.reduce((acc, f) => {
            if (f.hash) (acc[f.hash] ||= []).push(f);
            return acc;
        }, {});
        const duplicateGroups = Object.values(byHash)
            .filter(g => g.length > 1)
            // Within a group, oldest first (so the default keeper = the original).
            .map(g => [...g].sort((a, b) => toMillis(a?.metadata?.timeOfCreation) - toMillis(b?.metadata?.timeOfCreation)))
            .sort((a, b) => b.length - a.length);

        const totalBytes = files.reduce((s, f) => s + sizeOf(f), 0);

        // Storage by category.
        const byCategory = {};
        for (const f of files) {
            const c = categoryOf(f?.metadata?.mimetype);
            byCategory[c] = (byCategory[c] || 0) + sizeOf(f);
        }
        const categories = Object.entries(byCategory)
            .map(([name, bytes]) => ({ name, bytes }))
            .sort((a, b) => b.bytes - a.bytes);

        const largest = [...files].sort((a, b) => sizeOf(b) - sizeOf(a)).slice(0, 8);

        return {
            nameOf, sizeOf,
            missingHash, missingThumb, incompleteMeta,
            duplicateGroups, totalBytes, categories, largest,
            duplicateCount: duplicateGroups.reduce((n, g) => n + g.length - 1, 0),
        };
    }, [files]);

    const matchesSearch = useCallback((f) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (f?.metadata?.name || "").toLowerCase().includes(q) || (f.id || "").toLowerCase().includes(q);
    }, [search]);

    const keeperIdFor = useCallback((group) => {
        const hash = group[0]?.hash;
        const override = keeperByHash[hash];
        if (override && group.some(f => f.id === override)) return override;
        return group[0]?.id; // oldest (group is sorted oldest-first)
    }, [keeperByHash]);

    // Keeper-aware reclaimable total: bytes of every copy that ISN'T the chosen
    // keeper. Depends on keeperByHash (via keeperIdFor) so the headline figure
    // tracks the radio selection and matches what the bulk action actually deletes.
    const reclaimableBytes = useMemo(() => stats.duplicateGroups.reduce((sum, g) => {
        const keeperId = keeperIdFor(g);
        return sum + g.filter(f => f.id !== keeperId).reduce((s, f) => s + (Number(f?.metadata?.size) || 0), 0);
    }, 0), [stats.duplicateGroups, keeperIdFor]);

    // ── Processing-id helpers ────────────────────────────────────────────────
    const setProcessing = (id, on) => setProcessingIds(prev => {
        const next = new Set(prev);
        if (on) next.add(id); else next.delete(id);
        return next;
    });

    // ── Single-item actions (errors surfaced globally by the interceptor) ─────
    const handleGenerateHash = async (fileId) => {
        setProcessing(fileId, true);
        try {
            const newHash = await FileService.generateFileHash(fileId);
            setFiles(prev => prev.map(f => f.id === fileId ? { ...f, hash: newHash } : f));
        } catch { /* surfaced globally */ } finally {
            setProcessing(fileId, false);
        }
    };

    const handleRegenerateThumbnail = async (fileId) => {
        setProcessing(fileId, true);
        try {
            const res = await FileService.requestThumbnailRegeneration(fileId);
            const thumbnail = res?.data?.thumbnail ?? { regenerated: true };
            setFiles(prev => prev.map(f => f.id === fileId ? { ...f, thumbnail } : f));
        } catch { /* surfaced globally */ } finally {
            setProcessing(fileId, false);
        }
    };

    const handleDelete = async (fileId) => {
        const f = files.find(x => x.id === fileId);
        const ok = await confirm({
            title: 'Delete file?',
            message: `Delete "${f?.metadata?.name || fileId}"? This removes the file and all its versions.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            danger: true,
        });
        if (!ok) return;
        setProcessing(fileId, true);
        try {
            await FileService.deleteFile(fileId);
            setFiles(prev => prev.filter(x => x.id !== fileId));
        } catch { /* surfaced globally */ } finally {
            setProcessing(fileId, false);
        }
    };

    // ── Bulk actions (one summary toast instead of per-item) ──────────────────
    const runBulk = async (label, items, op) => {
        if (!items.length) return;
        setBulk({ label, done: 0, total: items.length });
        let ok = 0, failed = 0;
        for (const item of items) {
            try { await op(item); ok++; }
            catch { failed++; }
            setBulk(b => (b ? { ...b, done: b.done + 1 } : b));
        }
        setBulk(null);
        if (failed === 0) {
            notify({ type: 'success', title: 'Maintenance complete', message: `${label}: ${ok} processed.` });
        } else {
            notify({
                type: 'warning',
                title: 'Maintenance finished with errors',
                message: `${label}: ${ok} succeeded, ${failed} failed.`,
            });
        }
    };

    const handleGenerateAllHashes = () => runBulk("Generate hashes", stats.missingHash, async (file) => {
        const newHash = await FileService.generateFileHash(file.id, { skipGlobalErrorToast: true });
        setFiles(prev => prev.map(f => f.id === file.id ? { ...f, hash: newHash } : f));
    });

    const handleRegenerateAllThumbnails = () => runBulk("Regenerate thumbnails", stats.missingThumb, async (file) => {
        const res = await FileService.requestThumbnailRegeneration(file.id, { skipGlobalErrorToast: true });
        const thumbnail = res?.data?.thumbnail ?? { regenerated: true };
        setFiles(prev => prev.map(f => f.id === file.id ? { ...f, thumbnail } : f));
    });

    const handleResolveGroup = async (group) => {
        const keeperId = keeperIdFor(group);
        const toDelete = group.filter(f => f.id !== keeperId);
        if (!toDelete.length) return;
        const reclaim = formatBytes(toDelete.reduce((s, f) => s + stats.sizeOf(f), 0));
        const keeper = group.find(f => f.id === keeperId);
        const ok = await confirm({
            title: 'Resolve duplicate group?',
            message: `Keep "${keeper?.metadata?.name || keeperId}" and delete ${toDelete.length} duplicate copy(ies)? This frees about ${reclaim}.`,
            confirmText: 'Resolve',
            cancelText: 'Cancel',
            danger: true,
        });
        if (!ok) return;
        await runBulk("Resolve duplicates", toDelete, async (file) => {
            await FileService.deleteFile(file.id, { skipGlobalErrorToast: true });
            setFiles(prev => prev.filter(f => f.id !== file.id));
        });
    };

    const handleResolveAll = async () => {
        const toDelete = stats.duplicateGroups.flatMap(g => {
            const keeperId = keeperIdFor(g);
            return g.filter(f => f.id !== keeperId);
        });
        if (!toDelete.length) return;
        const ok = await confirm({
            title: 'Resolve all duplicates?',
            message: `Resolve all ${stats.duplicateGroups.length} duplicate group(s)? This deletes ${toDelete.length} redundant copy(ies) and frees about ${formatBytes(reclaimableBytes)}. One copy of each is kept.`,
            confirmText: 'Resolve all',
            cancelText: 'Cancel',
            danger: true,
        });
        if (!ok) return;
        await runBulk("Resolve duplicates", toDelete, async (file) => {
            await FileService.deleteFile(file.id, { skipGlobalErrorToast: true });
            setFiles(prev => prev.filter(f => f.id !== file.id));
        });
    };

    const busy = bulk != null;

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderCappedList = (items, renderRow) => {
        const shown = items.slice(0, ROW_CAP);
        return (
            <>
                {shown.map(renderRow)}
                {items.length > ROW_CAP && (
                    <div className="dm-row dm-row--note">
                        Showing first {ROW_CAP} of {items.length}. Bulk actions apply to all.
                    </div>
                )}
            </>
        );
    };

    const tabs = [
        { key: 'overview',   label: 'Overview',   icon: faDatabase },
        { key: 'duplicates', label: 'Duplicates', icon: faClone,       count: stats.duplicateGroups.length },
        { key: 'hashes',     label: 'Hashes',     icon: faFingerprint, count: stats.missingHash.length },
        { key: 'thumbs',     label: 'Thumbnails', icon: faImage,       count: stats.missingThumb.length },
    ];

    // Arrow-key navigation for the tablist (WAI-ARIA tabs pattern), with roving
    // tabindex applied on the buttons below.
    const handleTabKey = (e) => {
        const idx = tabs.findIndex(t => t.key === activeTab);
        let nextIdx = null;
        if (e.key === 'ArrowRight') nextIdx = (idx + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') nextIdx = 0;
        else if (e.key === 'End') nextIdx = tabs.length - 1;
        if (nextIdx === null) return;
        e.preventDefault();
        const key = tabs[nextIdx].key;
        setActiveTab(key);
        document.getElementById(`dm-tab-${key}`)?.focus();
    };

    return (
        <Modal
            className="uploadFileModal duplicateManagerModal fileMaintenanceModal modal--chrome"
            onClose={onClose}
            aria-labelledby="dm-modal-title"
        >
            {/* 1. Header */}
            <div className="modal-header">
                <h1 id="dm-modal-title">File Maintenance</h1>
                <ClosingX onClick={onClose} />
            </div>

            {/* Toolbar: search + refresh. Search is scoped to the list tabs — the
                Overview is a summary, so the box is hidden there to avoid a no-op. */}
            <div className="dm-toolbar">
                {activeTab !== 'overview' && (
                    <div className="dm-search">
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                        <input
                            type="text"
                            placeholder="Filter by name or ID…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Filter files"
                        />
                    </div>
                )}
                <button className="dm-icon-btn" onClick={loadFiles} disabled={loading || busy} title="Reload files" aria-label="Reload files">
                    <FontAwesomeIcon icon={faRotate} spin={loading} />
                </button>
            </div>

            {/* Tabs */}
            <div className="dm-tabs" role="tablist" aria-label="Maintenance sections" onKeyDown={handleTabKey}>
                {tabs.map(t => (
                    <button
                        key={t.key}
                        id={`dm-tab-${t.key}`}
                        role="tab"
                        aria-selected={activeTab === t.key}
                        aria-controls="dm-tabpanel"
                        tabIndex={activeTab === t.key ? 0 : -1}
                        className={`dm-tab ${activeTab === t.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(t.key)}
                    >
                        <FontAwesomeIcon icon={t.icon} />
                        <span>{t.label}</span>
                        {t.count > 0 && <span className={`dm-tab-badge ${t.key === 'duplicates' ? 'danger' : ''}`}>{t.count}</span>}
                    </button>
                ))}
            </div>

            {/* Bulk progress bar. The determinate value lives on role="progressbar";
                the textual label is aria-hidden so screen readers aren't re-announced
                on every tick (the final notify() summary covers completion). */}
            {bulk && (
                <div className="dm-progress">
                    <div className="dm-progress-label" aria-hidden="true">{bulk.label}… {bulk.done}/{bulk.total}</div>
                    <div
                        className="dm-progress-track"
                        role="progressbar"
                        aria-label={bulk.label}
                        aria-valuemin={0}
                        aria-valuemax={bulk.total}
                        aria-valuenow={bulk.done}
                    >
                        <div className="dm-progress-fill" style={{ width: `${bulk.total ? (bulk.done / bulk.total) * 100 : 0}%` }} />
                    </div>
                </div>
            )}

            {/* 2. Body */}
            <div className="modal-body" id="dm-tabpanel" role="tabpanel" aria-labelledby={`dm-tab-${activeTab}`} tabIndex={0}>

                {loading && <div className="empty-state-success">Loading files…</div>}

                {!loading && loadError && (
                    <div className="dm-error-banner">
                        <InlineBanner variant="danger" title="Couldn’t load files.">
                            {loadError}
                        </InlineBanner>
                        <BButton onClick={loadFiles} className="btn-small dm-error-retry">Retry</BButton>
                    </div>
                )}

                {!loading && !loadError && (
                    <>
                        {activeTab === 'overview'   && renderOverview()}
                        {activeTab === 'duplicates' && renderDuplicates()}
                        {activeTab === 'hashes'     && renderHashes()}
                        {activeTab === 'thumbs'     && renderThumbnails()}
                    </>
                )}
            </div>

            {/* 3. Footer */}
            <div className="modal-footer modal-footer--split">
                <div className="dm-footer-stat">
                    {files.length} file{files.length === 1 ? '' : 's'} · {formatBytes(stats.totalBytes)}
                    {reclaimableBytes > 0 && <> · <strong>{formatBytes(reclaimableBytes)} reclaimable</strong></>}
                </div>
                <div className="modal-actions">
                    <BButton onClick={onClose} className="btn-secondary">Close</BButton>
                </div>
            </div>
        </Modal>
    );

    // ── Tab renderers (closures — keep render() above readable) ───────────────

    function renderOverview() {
        const { totalBytes, categories, largest, missingHash, missingThumb, incompleteMeta, duplicateGroups } = stats;
        const healthy = !duplicateGroups.length && !missingHash.length && !missingThumb.length && !incompleteMeta.length;
        return (
            <div className="dm-section">
                <div className="dm-stat-grid">
                    <StatCard icon={faBoxArchive} value={files.length} label="Total files" />
                    <StatCard icon={faDatabase} value={formatBytes(totalBytes)} label="Storage used" />
                    <StatCard icon={faClone} value={duplicateGroups.length} label="Duplicate groups"
                              tone={duplicateGroups.length ? 'warn' : 'ok'}
                              onClick={duplicateGroups.length ? () => setActiveTab('duplicates') : undefined} />
                    <StatCard icon={faWandMagicSparkles} value={formatBytes(reclaimableBytes)} label="Reclaimable"
                              tone={reclaimableBytes ? 'warn' : 'ok'}
                              onClick={reclaimableBytes ? () => setActiveTab('duplicates') : undefined} />
                    <StatCard icon={faFingerprint} value={missingHash.length} label="Missing hash"
                              tone={missingHash.length ? 'warn' : 'ok'}
                              onClick={missingHash.length ? () => setActiveTab('hashes') : undefined} />
                    <StatCard icon={faImage} value={missingThumb.length} label="Missing thumbnail"
                              tone={missingThumb.length ? 'warn' : 'ok'}
                              onClick={missingThumb.length ? () => setActiveTab('thumbs') : undefined} />
                </div>

                {healthy && (
                    <div className="empty-state-success" style={{ marginTop: 16 }}>
                        <FontAwesomeIcon icon={faCheckCircle} /> Everything looks healthy — no duplicates or missing data.
                    </div>
                )}

                {/* Storage by type */}
                {categories.length > 0 && totalBytes > 0 && (
                    <div className="dm-subsection">
                        <h4>Storage by type</h4>
                        <div className="dm-bar">
                            {categories.map(c => (
                                <div key={c.name} className="dm-bar-seg"
                                     style={{ width: `${(c.bytes / totalBytes) * 100}%`, background: CATEGORY_COLORS[c.name] }}
                                     title={`${c.name}: ${formatBytes(c.bytes)}`} />
                            ))}
                        </div>
                        <div className="dm-legend">
                            {categories.map(c => (
                                <span key={c.name} className="dm-legend-item">
                                    <span className="dm-legend-dot" style={{ background: CATEGORY_COLORS[c.name] }} />
                                    {c.name} · {formatBytes(c.bytes)}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Incomplete metadata (informational) */}
                {incompleteMeta.length > 0 && (
                    <div className="dm-subsection">
                        <h4><span className="badge-warning">⚠️</span> {incompleteMeta.length} file(s) with incomplete metadata</h4>
                        <p className="dm-hint">These are missing a name, type, or size — often broken or interrupted uploads worth reviewing.</p>
                        <div className="dm-list-container">
                            {renderCappedList(incompleteMeta, f => (
                                <div key={f.id} className="dm-row">
                                    <div className="dm-info">
                                        <span className="dm-name">{f?.metadata?.name || <em>Unnamed</em>}</span>
                                        <span className="dm-meta">ID: {f.id} · {f?.metadata?.mimetype || 'unknown type'} · {formatBytes(f?.metadata?.size)}</span>
                                    </div>
                                    <button className="dm-action-btn danger" onClick={() => handleDelete(f.id)}
                                            disabled={processingIds.has(f.id) || busy} title="Delete this file"
                                            aria-label={`Delete ${f?.metadata?.name || f.id}`}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Largest files */}
                {largest.length > 0 && (
                    <div className="dm-subsection">
                        <h4>Largest files</h4>
                        <div className="dm-list-container">
                            {largest.map(f => (
                                <div key={f.id} className="dm-row">
                                    <div className="dm-info">
                                        <span className="dm-name">{f?.metadata?.name || 'Untitled'}</span>
                                        <span className="dm-meta">{f?.metadata?.mimetype || 'unknown'} · {formatDate(f?.metadata?.timeOfCreation)}</span>
                                    </div>
                                    <span className="dm-size-pill">{formatBytes(f?.metadata?.size)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    function renderHashes() {
        const items = stats.missingHash.filter(matchesSearch);
        return (
            <div className="dm-section">
                <div className="dm-section-header">
                    <h3>
                        {stats.missingHash.length > 0 && <span className="badge-warning">⚠️</span>}
                        Files Missing Hash ({stats.missingHash.length})
                    </h3>
                    {stats.missingHash.length > 0 && (
                        <BButton onClick={handleGenerateAllHashes} enabled={!busy} className="btn-small">
                            Generate All
                        </BButton>
                    )}
                </div>
                <p className="dm-hint">Content hashes power duplicate detection. Files without one are excluded from the duplicate scan.</p>

                {stats.missingHash.length === 0 ? (
                    <div className="empty-state-success">
                        <FontAwesomeIcon icon={faCheckCircle} /> All files have hashes calculated.
                    </div>
                ) : items.length === 0 ? (
                    <div className="dm-row dm-row--note">No files match “{search}”.</div>
                ) : (
                    <div className="dm-list-container">
                        {renderCappedList(items, f => (
                            <div key={f.id} className="dm-row">
                                <div className="dm-info">
                                    <span className="dm-name">{f?.metadata?.name || 'Untitled'}</span>
                                    <span className="dm-meta">ID: {f.id} · {formatBytes(f?.metadata?.size)}</span>
                                </div>
                                <button className="dm-action-btn primary" onClick={() => handleGenerateHash(f.id)}
                                        disabled={processingIds.has(f.id) || busy}>
                                    {processingIds.has(f.id) ? "…" : "Generate"}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    function renderThumbnails() {
        const items = stats.missingThumb.filter(matchesSearch);
        return (
            <div className="dm-section">
                <div className="dm-section-header">
                    <h3>
                        {stats.missingThumb.length > 0 && <span className="badge-warning">⚠️</span>}
                        Files Missing Thumbnail ({stats.missingThumb.length})
                    </h3>
                    {stats.missingThumb.length > 0 && (
                        <BButton onClick={handleRegenerateAllThumbnails} enabled={!busy} className="btn-small">
                            Regenerate All
                        </BButton>
                    )}
                </div>
                <p className="dm-hint">Thumbnails are generated on the server. Regenerating can recover previews for files that failed to process on upload.</p>

                {stats.missingThumb.length === 0 ? (
                    <div className="empty-state-success">
                        <FontAwesomeIcon icon={faCheckCircle} /> Every file has a thumbnail.
                    </div>
                ) : items.length === 0 ? (
                    <div className="dm-row dm-row--note">No files match “{search}”.</div>
                ) : (
                    <div className="dm-list-container">
                        {renderCappedList(items, f => (
                            <div key={f.id} className="dm-row">
                                <div className="dm-info">
                                    <span className="dm-name">{f?.metadata?.name || 'Untitled'}</span>
                                    <span className="dm-meta">ID: {f.id} · {f?.metadata?.mimetype || 'unknown type'}</span>
                                </div>
                                <button className="dm-action-btn primary" onClick={() => handleRegenerateThumbnail(f.id)}
                                        disabled={processingIds.has(f.id) || busy}>
                                    {processingIds.has(f.id) ? "…" : "Regenerate"}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    function renderDuplicates() {
        const groups = stats.duplicateGroups.filter(
            g => !search.trim() || g.some(matchesSearch)
        );
        return (
            <div className="dm-section">
                <div className="dm-section-header">
                    <h3>
                        {stats.duplicateGroups.length > 0 && <span className="badge-danger">!</span>}
                        Duplicate Groups ({stats.duplicateGroups.length})
                    </h3>
                    {stats.duplicateGroups.length > 0 && (
                        <BButton onClick={handleResolveAll} enabled={!busy} className="btn-small btn-danger">
                            Resolve all · free {formatBytes(reclaimableBytes)}
                        </BButton>
                    )}
                </div>
                <p className="dm-hint">Files with identical content. Pick the copy to keep (defaults to the oldest); the rest can be deleted in one click.</p>

                {stats.duplicateGroups.length === 0 ? (
                    <div className="empty-state-success">
                        <FontAwesomeIcon icon={faCheckCircle} /> No duplicate files found.
                    </div>
                ) : groups.length === 0 ? (
                    <div className="dm-row dm-row--note">No duplicate groups match “{search}”.</div>
                ) : (
                    <div className="dm-groups-container">
                        {groups.map((group) => {
                            const keeperId = keeperIdFor(group);
                            const reclaim = group.filter(f => f.id !== keeperId).reduce((s, f) => s + stats.sizeOf(f), 0);
                            return (
                                <div key={group[0].hash} className="dm-card">
                                    <div className="dm-card-header">
                                        <span className="dm-hash-label">Hash</span>
                                        <code className="dm-hash-val">{String(group[0].hash).substring(0, 16)}…</code>
                                        <span className="dm-card-spacer" />
                                        <span className="dm-meta">{group.length} copies · free {formatBytes(reclaim)}</span>
                                        <button className="dm-action-btn danger dm-resolve-btn"
                                                onClick={() => handleResolveGroup(group)}
                                                disabled={busy}
                                                title="Keep the selected copy, delete the rest">
                                            Resolve
                                        </button>
                                    </div>
                                    <div className="dm-card-body" role="radiogroup" aria-label="Choose which copy to keep">
                                        {group.map(f => {
                                            const isKeeper = f.id === keeperId;
                                            return (
                                                <div key={f.id} className={`dm-row ${isKeeper ? 'dm-row--keeper' : ''}`}>
                                                    <label className="dm-keeper-pick">
                                                        <input
                                                            type="radio"
                                                            name={`keep-${group[0].hash}`}
                                                            checked={isKeeper}
                                                            onChange={() => setKeeperByHash(prev => ({ ...prev, [group[0].hash]: f.id }))}
                                                            disabled={busy}
                                                            aria-label={`Keep ${f?.metadata?.name || f.id}`}
                                                        />
                                                        <div className="dm-info">
                                                            <span className="dm-name">
                                                                {f?.metadata?.name || 'Untitled'}
                                                                {isKeeper && <span className="dm-keeper-tag">Keep</span>}
                                                            </span>
                                                            <span className="dm-meta">
                                                                {formatBytes(f?.metadata?.size)} · {formatDate(f?.metadata?.timeOfCreation)}
                                                            </span>
                                                        </div>
                                                    </label>
                                                    <button
                                                        className="dm-action-btn danger"
                                                        onClick={() => handleDelete(f.id)}
                                                        disabled={processingIds.has(f.id) || busy}
                                                        title="Delete this copy"
                                                        aria-label={`Delete this copy of ${f?.metadata?.name || f.id}`}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
};

export default DuplicateManagerModal;
